import { cellAtWorld } from '../grid.ts';
import { dist2, iatan2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { WolfRules, WolfState } from '../roca-types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { spawnCub } from './mouths.ts';
import { allyStrike, blowHorn, rocaNpc } from './roca-ally.ts';
import { ROCA_HORN } from './climb.ts';

/**
 * The Grey Wolf (docs/sendas/barbaro.md, chapter 5, act 4, the path's enemy; §5): an ash wolf the size of a horse, its
 * coat coming apart into smoke at the ends, red eyes. Rapid; never still. Its rule in three seconds: when it runs, don't
 * let it reach the water.
 *
 * 1. In the den, on the ash bed among the cubs: each time it loses a share of its life it looks three seconds at the
 *    open mouth nearest (marked red) and runs for it as fast as the hero. Standing at that mouth as it gets there, or
 *    enough blows on the way, cut it; a slab with two cracks on its way gives under it and holds it. Reaching the
 *    water it is gone a while and comes back healed (less with Sigrun's axe; nothing by the mouth without its tracks,
 *    stones below). The mouths shut in act 2 are shut here.
 * 2. At 60 % it goes up the crack in the roof to the summit: wind, the sea below, three dark edges over deep water. The
 *    clan's boats row under one edge and close it (the next every so often, never the one already shut: with the axe
 *    left in the ice the edge toward the island is shut for good). Ivar comes up behind it and fights beside the hero.
 * 3. At 25 % (40 % with all four mouths shut) the last run: it doesn't run or howl any more, it hunts: faster, leaping
 *    more often; with the hero low it goes down the wall for Sten's small boat (broken, the try is lost).
 *
 * Every so often it bristles and howls; let through, the pack and the cubs come back. A pillar of the den's roof or a
 * stone of the summit's edge brought down on it, Ulla's horn, or enough of its life struck cut it, and it reels a
 * moment taking more. Its leap: its shadow marked where the hero stands, three seconds, then it lands there.
 *
 * Cost: one boss; the pack as any band; the slabs four cells looked at every other tick; the boats one number.
 */

/** The marks the hero can be sent to (the `mark` command's `at`): a pillar, a stone (and the horn, ROCA_HORN). */
export const WOLF_PILLAR = 0;
export const WOLF_STONE = 10;

const wr = (s: BattleState): WolfRules | undefined => s.rules?.senda?.roca?.wolf;
const ws = (s: BattleState): WolfState | undefined => s.senda?.roca?.wolf;

const lifeBp = (e: Enemy): number => (e.maxHp > 0 ? idiv(imax(0, e.hp) * 10000, e.maxHp) : 0);

/** The edges not shut for good (the island's, with the axe left in the ice). */
const edgesFree = (r: WolfRules): number[] => r.edges.map((_, k) => k).filter((k) => r.axe || k !== r.island);

/** Whether a summit's edge is shut now (the boats under it, or the island's for good). */
export const edgeShut = (r: WolfRules, w: WolfState, k: number): boolean => k === w.boats || (!r.axe && k === r.island);

/** The phase 3 begins at (bp of its life): sooner with no mouth left open in the den. */
const lastAt = (r: WolfRules): number => (r.open.some((v) => v) ? r.phase3Bp : r.phase3ShutBp);

const fresh = (s: BattleState, r: WolfRules): WolfState => {
  const e = spawnEnemy(s, 'wolf', 1, false, 0, r.bed.x, r.bed.y - 500, r.hp);
  return {
    id: e.id,
    phase: 1,
    mode: 0,
    until: 0,
    exit: -1,
    exitKind: 0,
    hits: 0,
    hpWas: e.hp,
    fleeAt: e.maxHp - mulBp(e.maxHp, r.fleeBp),
    slowBp: 0,
    cd: 0,
    leapNext: r.leap.every,
    leapAt: { x: 0, y: 0 },
    leapFrom: { x: 0, y: 0 },
    howlNext: r.howl.first,
    howlHp: e.hp,
    exposed: 0,
    boats: -1,
    boatsNext: 0,
    boatMoves: 0,
    horn: r.horn.uses,
    scared: 0,
    pillars: r.pillars.map(() => 1),
    stones: r.stones.map(() => 1),
    slabs: r.slabs.map((x) => x.cracks),
    onSlab: -1,
    job: -1,
    t0: -1,
    ivarIn: false,
    ivarCd: 0,
    ullaCd: 0,
    boatRunNext: 0,
    boatHits: 0,
    gnawCd: 0,
    cubsNext: r.cubs.first,
    cubTurn: 0,
    looks: 0,
    dives: 0,
    cuts: 0,
    howls: 0,
    howlCuts: 0,
    leaps: 0,
    bites: 0,
    boatRuns: 0,
    done: -1,
    lost: -1,
  };
};

/** The Wolf's state, if this nightmare is it. */
export const wolfOf = (s: BattleState, id: number): WolfState | undefined => {
  const w = ws(s);
  return w && w.id === id ? w : undefined;
};

/** Under the water nothing reaches it. */
export const wolfSeen = (s: BattleState, e: Enemy): boolean => {
  const w = ws(s);
  return !w || w.id !== e.id || w.mode !== 3;
};

/** A cut howl leaves it taking more a moment. */
export const wolfTakes = (s: BattleState, e: Enemy, dmg: number): number => {
  const r = wr(s);
  const w = ws(s);
  return r && w && w.id === e.id && w.exposed > 0 ? mulBp(dmg, 10000 + r.howl.takenBp) : dmg;
};

/** The Wolf's blows are its own (the path's base doesn't count them on Ivar). */
export const wolfOwnBlow = (s: BattleState, e: Enemy): boolean => ws(s)?.id === e.id;

/** Where its flight goes now: the open mouth (den) or the free edge (summit) nearest it, −1 none. */
export const wolfExit = (r: WolfRules, w: WolfState, x: number, y: number): number => {
  let best = -1;
  let bd = 0x7fffffff;
  const list = w.phase === 1 ? r.mouths : r.edges;
  list.forEach((p, k) => {
    if (w.phase === 1 ? !r.open[k] : edgeShut(r, w, k)) return;
    const d = dist2(p.x, p.y, x, y);
    if (d < bd) {
      bd = d;
      best = k;
    }
  });
  return best;
};

/** The point of a way out (a mouth or an edge). */
export const wolfExitAt = (r: WolfRules, kind: number, k: number): Pt => ((kind === 0 ? r.mouths[k] : r.edges[k]) ?? r.bed) as Pt;

/** The point where the hero stands to bring down a pillar or a stone (on the floor beside it). */
export const wolfDropAt = (r: WolfRules, at: number): Pt | null => {
  const p = at >= WOLF_STONE ? r.stones[at - WOLF_STONE] : r.pillars[at - WOLF_PILLAR];
  if (!p) return null;
  const c = at >= WOLF_STONE ? r.summit : r.bed;
  const dx = c.x - p.x;
  const dy = c.y - p.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  return { x: p.x + idiv(dx * 1150, d), y: p.y + idiv(dy * 1150, d) };
};

const cut = (s: BattleState, r: WolfRules, w: WolfState, e: Enemy, how: number, cue: (c: string) => void): void => {
  w.mode = 4;
  w.until = s.tick + r.stun;
  w.cuts++;
  e.stun = imax(e.stun, r.stun);
  // (Cut off from the water it reels as from a cut howl: open to every blow a moment.)
  w.exposed = r.stun;
  w.fleeAt = imax(1, imin(w.fleeAt, e.hp - mulBp(e.maxHp, r.fleeBp)));
  ev(s, { t: 'senda', kind: 'rCut', npc: '', x: e.x, y: e.y, n: how });
  cue(`cut${w.cuts}`);
};

const cutHowl = (s: BattleState, r: WolfRules, w: WolfState, e: Enemy, how: number, cue: (c: string) => void): void => {
  w.mode = 4;
  w.until = s.tick + r.howl.stun;
  e.stun = imax(e.stun, r.howl.stun);
  w.exposed = r.howl.stun;
  w.howlCuts++;
  w.howlNext = s.waveT + r.howl.every;
  ev(s, { t: 'senda', kind: 'rHowlCut', npc: '', x: e.x, y: e.y, n: how });
  cue(`howlCut${w.howlCuts}`);
};

/** Into the next phase: up the crack to the summit (2), or the last run (3). */
const phaseTo = (s: BattleState, r: WolfRules, w: WolfState, e: Enemy, p: number, cue: (c: string) => void): void => {
  w.phase = p;
  w.horn = r.horn.uses;
  w.job = -1;
  if (p === 2) {
    w.mode = 9;
    w.fleeAt = imax(1, e.hp - mulBp(e.maxHp, r.fleeBp));
    w.howlNext = imax(w.howlNext, s.waveT + r.howl.first);
    ev(s, { t: 'senda', kind: 'rUp', npc: '', x: r.crackTop.x, y: r.crackTop.y });
    cue('up');
    return;
  }
  w.mode = 0;
  w.leapNext = imin(w.leapNext, s.waveT + r.leap.last);
  ev(s, { t: 'senda', kind: 'rLast', npc: '', x: e.x, y: e.y });
  cue('last');
};

/** The clan's boats row under the next edge (or the first, as the Wolf comes up). */
const rowBoats = (s: BattleState, r: WolfRules, w: WolfState, cue: (c: string) => void): void => {
  const free = edgesFree(r);
  const at = free.indexOf(w.boats);
  w.boats = free[(at + 1) % free.length] as number;
  w.boatsNext = s.waveT + r.boats.every;
  w.boatMoves++;
  const p = r.edges[w.boats] as Pt;
  ev(s, { t: 'senda', kind: 'rBoats', npc: '', x: p.x, y: p.y, n: w.boats });
  cue(`boats${w.boatMoves}`);
};

/** The pack back (a howl let through): wolves and cubs off the den's rim or up the summit's. */
const raise = (s: BattleState, r: WolfRules, w: WolfState): void => {
  const rim = w.phase === 1 ? r.denRim : r.topRim;
  const p = rim[rngRange(s.rng, 0, rim.length - 1)] as Pt;
  const [nw, nc] = r.howl.raise;
  for (let i = 0; i < nw; i++) spawnEnemy(s, 'wolf', 1, false, 0, p.x + ((i % 3) - 1) * 450, p.y, r.wolfHp);
  for (let i = 0; i < nc; i++) spawnCub(s, p.x + ((i % 3) - 1) * 380, p.y + 400, r.cubHp, r.cubDmgBp);
};

/** A pillar or a stone brought down: on the Wolf if it stands under it (a howl cut, or a stumble). */
const drop = (s: BattleState, r: WolfRules, w: WolfState, at: number, cue: (c: string) => void): void => {
  const stone = at >= WOLF_STONE;
  const k = stone ? at - WOLF_STONE : at - WOLF_PILLAR;
  const list = stone ? w.stones : w.pillars;
  const p = (stone ? r.stones[k] : r.pillars[k]) as Pt;
  if (!list[k]) return;
  list[k] = 0;
  const e = s.enemies.find((x) => x.id === w.id && x.hp > 0);
  const on = !!e && w.mode !== 3 && withinRange(e.x, e.y, p.x, p.y, r.drop.r);
  ev(s, { t: 'senda', kind: 'rDrop', npc: '', x: p.x, y: p.y, n: on ? 1 : 0 });
  if (!e || !on) return;
  if (w.mode === 7 || w.mode === 8) cutHowl(s, r, w, e, stone ? 2 : 1, cue);
  else e.stun = imax(e.stun, 12);
};

/** The hero's tap: a pillar, a stone (to bring down on it) or the horn. */
export const wolfOrder = (s: BattleState, h: Hero, at: number, cue: (c: string) => void): void => {
  const r = wr(s);
  const w = ws(s);
  if (!r || !w || w.done >= 0) return;
  if (at === ROCA_HORN) {
    if (w.horn <= 0) return;
    w.horn--;
    const e = s.enemies.find((x) => x.id === w.id && x.hp > 0);
    blowHorn(s, rocaNpc(s, 'ulla'), r.horn, w.id);
    if (e && (w.mode === 7 || w.mode === 8)) cutHowl(s, r, w, e, 3, cue);
    cue('horn');
    return;
  }
  const stone = at >= WOLF_STONE && at < WOLF_STONE + r.stones.length;
  const pillar = at >= WOLF_PILLAR && at < WOLF_PILLAR + r.pillars.length;
  if ((!stone && !pillar) || (stone && !w.stones[at - WOLF_STONE]) || (pillar && !w.pillars[at - WOLF_PILLAR])) return;
  // (A pillar is the den's, a stone the summit's: the one where the fight is.)
  if ((stone && w.phase === 1) || (pillar && w.phase > 1)) return;
  const p = wolfDropAt(r, at);
  if (!p) return;
  w.job = at;
  w.t0 = -1;
  orderMove(s, h, p.x, p.y);
};

/** Another order of the hero's own: what it was sent to bring down is off. */
export const wolfOff = (s: BattleState): void => {
  const w = ws(s);
  if (w) {
    w.job = -1;
    w.t0 = -1;
  }
};

/** The slabs the hero comes onto crack (a third time, it gives under it); the Wolf running over a cracked one is caught. */
const slabsTick = (s: BattleState, r: WolfRules, w: WolfState, h: Hero | undefined, e: Enemy): void => {
  if (w.phase !== 1 || s.tick % 2) return;
  if (h) {
    const c = cellAtWorld(s, h.x, h.y);
    const i = r.slabs.findIndex((x) => x.cell === c);
    if (i < 0) w.onSlab = -1;
    else if (i !== w.onSlab) {
      w.onSlab = i;
      const n = (w.slabs[i] as number) + 1;
      if ((w.slabs[i] as number) < 3) {
        w.slabs[i] = imin(3, n);
        const p = (r.slabs[i] as { at: Pt }).at;
        ev(s, { t: 'senda', kind: 'rSlab', npc: '', x: p.x, y: p.y, n: w.slabs[i] });
        if (n >= 3) {
          damageHero(s, h, mulBp(h.stats.maxHp, r.slab.fallBp), null, 'physical', true);
          ev(s, { t: 'senda', kind: 'rSlabGive', npc: '', x: p.x, y: p.y, n: i });
        }
      }
    }
  }
  if (w.mode === 2) {
    const c = cellAtWorld(s, e.x, e.y);
    const i = r.slabs.findIndex((x) => x.cell === c);
    if (i >= 0 && w.slabs[i] === 2) {
      w.slabs[i] = 3;
      w.mode = 11;
      w.until = s.tick + r.slab.stuck;
      e.stun = imax(e.stun, r.slab.stuck);
      const p = (r.slabs[i] as { at: Pt }).at;
      ev(s, { t: 'senda', kind: 'rStuck', npc: '', x: p.x, y: p.y, n: i });
    }
  }
};

/** Ivar on the summit: a step behind the hero, a blow at what is near. Ulla where she stands (the horn's). */
const alliesTick = (s: BattleState, r: WolfRules, w: WolfState, h: Hero | undefined): void => {
  const iv = rocaNpc(s, 'ivar');
  if (w.ivarIn && iv && iv.hp > 0 && h) {
    iv.moving = false;
    if (!withinRange(iv.x, iv.y, h.x, h.y, r.ivar.follow)) {
      iv.moving = true;
      stepToward(iv, h.x, h.y, r.ivar.speed);
      iv.facing = iatan2(h.y - iv.y, h.x - iv.x);
    } else w.ivarCd = allyStrike(s, iv, r.ivar, w.ivarCd, (x) => x.id === w.id && w.mode === 3);
  }
};

/**
 * A bite on the hero (with Sigrun's axe it goes for the hero alone); without it Ivar covers the hero: at it too, he takes
 * `ivar.coverBp` of the bites (the two branches measured even, docs/DECISIONS.md). On Ivar with no hero standing.
 */
const bite = (s: BattleState, r: WolfRules, w: WolfState, e: Enemy, h: Hero | undefined): boolean => {
  const iv = rocaNpc(s, 'ivar');
  const near = !!iv && w.ivarIn && iv.hp > 0 && withinRange(iv.x, iv.y, e.x, e.y, r.reach);
  const ivar = iv && near && (!h || (!r.axe && w.cd === 0 && rngInt(s.rng, 10000) < r.ivar.coverBp)) ? iv : null;
  const t: Pt | null = ivar ?? h ?? null;
  if (!t) return false;
  if (!withinRange(e.x, e.y, t.x, t.y, r.reach)) {
    e.anim = 'walk';
    const pace = idiv(mulBp(r.speed, w.phase === 3 ? r.huntBp : 10000), 12);
    stepToward(e, t.x, t.y, pace);
    return true;
  }
  if (t.x !== e.x || t.y !== e.y) e.facing = iatan2(t.y - e.y, t.x - e.x);
  if (w.cd === 0) {
    w.cd = r.reload;
    e.anim = 'attack';
    e.animT = 0;
    w.bites++;
    if (ivar) hurtNpc(s, ivar);
    // (Its bite goes through armour, as the leap does.)
    // (Back from the water angrier each time: harder for every dive.)
    else if (h) damageHero(s, h, mulBp(mulBp(h.stats.maxHp, r.biteBp), 10000 + r.diveBiteBp * w.dives), e, 'physical', true);
  } else if (e.anim !== 'attack' || e.animT > 6) e.anim = 'idle';
  return true;
};

/** A blow on a person of the path (Ivar, Sten's boat): its life in blows. */
const hurtNpc = (s: BattleState, n: SendaNpc): void => {
  if (n.hp <= 0) return;
  n.hp = imax(0, n.hp - 1);
  ev(s, { t: 'senda', kind: 'hit', npc: n.id, x: n.x, y: n.y });
  if (n.hp <= 0) ev(s, { t: 'senda', kind: 'down', npc: n.id, x: n.x, y: n.y });
};

export const wolfTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = wr(s);
  const st = s.senda;
  if (!r || !st) return;
  const rs = (st.roca ??= {});
  const w = (rs.wolf ??= fresh(s, r));
  if (!s.started || w.done >= 0 || w.lost >= 0) return;
  const e = s.enemies.find((x) => x.id === w.id);
  if (!e || e.hp <= 0) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'rAsh', npc: '', x: e?.x ?? 0, y: e?.y ?? 0 });
    cue('down');
    return;
  }
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (w.cd > 0) w.cd--;
  if (w.exposed > 0) w.exposed--;
  // The hero at what it was sent to bring down: a moment there, and down it comes.
  if (h && w.job >= 0) {
    const p = wolfDropAt(r, w.job);
    if (p && withinRange(h.x, h.y, p.x, p.y, r.drop.reach) && h.stun === 0) {
      if (w.t0 < 0) w.t0 = s.tick;
      else if (s.tick - w.t0 >= r.drop.strike) {
        const at = w.job;
        w.job = -1;
        w.t0 = -1;
        drop(s, r, w, at, cue);
      }
    } else w.t0 = -1;
  }
  slabsTick(s, r, w, h, e);
  // The phases, by its life (never while it is under the water or in the air).
  const life = lifeBp(e);
  if (w.mode !== 3 && w.mode !== 6 && w.mode !== 9) {
    if (w.phase === 1 && life <= r.phase2Bp) {
      phaseTo(s, r, w, e, 2, cue);
      // Ulla comes up to the crack's top with her horn; Ivar ties his wound and comes up behind it.
      const u = rocaNpc(s, 'ulla');
      if (u) Object.assign(u, { x: r.crackTop.x - 900, y: r.crackTop.y + 300 });
      const iv = rocaNpc(s, 'ivar');
      if (iv && iv.hp > 0) {
        Object.assign(iv, { x: r.ivarUp.x, y: r.ivarUp.y });
        w.ivarIn = true;
        ev(s, { t: 'senda', kind: 'rIvar', npc: 'ivar', x: iv.x, y: iv.y });
        cue('ivarIn');
      }
      rowBoats(s, r, w, cue);
    } else if (w.phase === 2 && life <= lastAt(r)) phaseTo(s, r, w, e, 3, cue);
  }
  if (w.phase === 2 && s.waveT >= w.boatsNext) rowBoats(s, r, w, cue);
  // Cubs out of the den's side galleries (not the mouths: whoever guards one isn't swarmed for it).
  if (w.phase === 1 && s.waveT >= w.cubsNext) {
    w.cubsNext = s.waveT + r.cubs.every;
    const p = r.denRim[w.cubTurn % r.denRim.length] as Pt;
    w.cubTurn++;
    const n = rngRange(s.rng, r.cubs.n[0], r.cubs.n[1]);
    const toward = { x: r.bed.x - p.x, y: r.bed.y - p.y };
    const d = imax(1, isqrt(toward.x * toward.x + toward.y * toward.y));
    const x = p.x + idiv(toward.x * 800, d);
    const y = p.y + idiv(toward.y * 800, d);
    for (let i = 0; i < n; i++) spawnCub(s, x + ((i % 3) - 1) * 380, y + (i >> 1) * 340, r.cubHp, r.cubDmgBp);
  }
  modeTick(s, r, w, e, h, cue);
  alliesTick(s, r, w, h);
};

const modeTick = (s: BattleState, r: WolfRules, w: WolfState, e: Enemy, h: Hero | undefined, cue: (c: string) => void): void => {
  if (w.mode === 0) {
    if (e.stun > 0) return;
    if (w.phase < 3 && e.hp <= w.fleeAt) {
      w.fleeAt = imax(1, w.fleeAt - mulBp(e.maxHp, r.fleeBp));
      const k = wolfExit(r, w, e.x, e.y);
      if (k < 0) return;
      w.mode = 1;
      w.until = s.tick + r.look;
      w.exit = k;
      w.exitKind = w.phase === 1 ? 0 : 1;
      w.looks++;
      const p = wolfExitAt(r, w.exitKind, k);
      e.facing = iatan2(p.y - e.y, p.x - e.x);
      ev(s, { t: 'senda', kind: 'rLook', npc: '', x: p.x, y: p.y, n: k + w.exitKind * 10 });
      cue(`look${w.looks}`);
      return;
    }
    if (w.phase < 3 && s.waveT >= w.howlNext) {
      w.mode = 7;
      w.until = s.tick + r.howl.rear;
      w.howlHp = e.hp;
      w.howls++;
      ev(s, { t: 'senda', kind: 'rRear', npc: '', x: e.x, y: e.y, n: r.howl.rear });
      cue(`rear${w.howls}`);
      return;
    }
    if (h && s.waveT >= w.leapNext) {
      w.mode = 5;
      w.until = s.tick + r.leap.warn;
      w.leapAt = { x: h.x, y: h.y };
      w.leapFrom = { x: e.x, y: e.y };
      w.leaps++;
      e.facing = iatan2(h.y - e.y, h.x - e.x);
      ev(s, { t: 'senda', kind: 'rLeapAim', npc: '', x: h.x, y: h.y, n: r.leap.warn });
      return;
    }
    if (w.phase === 3 && h && s.waveT >= w.boatRunNext && idiv(h.hp * 10000, imax(1, h.stats.maxHp)) < r.boat.heroBp) {
      w.mode = 10;
      w.boatHits = 0;
      w.hpWas = e.hp;
      w.boatRuns++;
      w.gnawCd = r.boat.gnaw;
      ev(s, { t: 'senda', kind: 'rBoatRun', npc: '', x: r.skiff.x, y: r.skiff.y });
      cue(`boatRun${w.boatRuns}`);
    }
    return;
  }
  if (w.mode === 1) {
    if (s.tick >= w.until) {
      w.mode = 2;
      w.hits = 0;
      w.hpWas = e.hp;
      w.slowBp = 0;
      const p = wolfExitAt(r, w.exitKind, w.exit);
      ev(s, { t: 'senda', kind: 'rRun', npc: '', x: p.x, y: p.y, n: w.exit + w.exitKind * 10 });
    }
    return;
  }
  if (w.mode === 2 || w.mode === 11) {
    if (e.hp < w.hpWas) {
      w.hits++;
      w.slowBp = imin(10000 - r.minSlowBp, w.slowBp + r.slowBp);
    }
    w.hpWas = e.hp;
    if (w.hits >= r.caught) return cut(s, r, w, e, 1, cue);
    if (w.mode === 11) {
      if (s.tick >= w.until) w.mode = 2;
      return;
    }
    // On the summit, the boats rowed in under the edge it runs for: there's nowhere to go.
    if (w.exitKind === 1 && edgeShut(r, w, w.exit)) return cut(s, r, w, e, 2, cue);
    const p = wolfExitAt(r, w.exitKind, w.exit);
    // The hero guarding that way out as it comes near (there before it: nearer the water than it is), it turns.
    if (h && withinRange(e.x, e.y, p.x, p.y, r.block + 700) && withinRange(h.x, h.y, p.x, p.y, r.block) && dist2(h.x, h.y, p.x, p.y) < dist2(e.x, e.y, p.x, p.y)) return cut(s, r, w, e, 0, cue);
    if (withinRange(e.x, e.y, p.x, p.y, 700)) {
      w.mode = 3;
      w.until = s.tick + r.under;
      w.dives++;
      e.x = p.x;
      e.y = p.y;
      ev(s, { t: 'senda', kind: 'rDive', npc: '', x: p.x, y: p.y, n: w.exit + w.exitKind * 10 });
      cue(`dive${w.dives}`);
    }
    return;
  }
  if (w.mode === 3) {
    if (s.tick < w.until) return;
    const p = wolfExitAt(r, w.exitKind, w.exit);
    // Healed in the deep water (less with the axe); by the mouth without its tracks, stones below: nothing.
    const bare = w.exitKind === 0 && w.exit === r.trace;
    const heal = bare ? 0 : r.healBp;
    e.hp = imin(e.maxHp, e.hp + mulBp(e.maxHp, heal));
    w.fleeAt = imax(1, e.hp - mulBp(e.maxHp, r.fleeBp));
    // Up by the way it went, toward the middle.
    const c = w.exitKind === 0 ? r.bed : r.summit;
    const dx = c.x - p.x;
    const dy = c.y - p.y;
    const d = imax(1, isqrt(dx * dx + dy * dy));
    e.x = p.x + idiv(dx * 1100, d);
    e.y = p.y + idiv(dy * 1100, d);
    // (Cubs up out of the water with it.)
    for (let i = 0; i < r.rise; i++) spawnCub(s, e.x + ((i % 3) - 1) * 420, e.y + idiv(dy * 500, d), r.cubHp, r.cubDmgBp);
    w.mode = 0;
    w.hpWas = e.hp;
    w.leapNext = imax(w.leapNext, s.waveT + 36);
    ev(s, { t: 'senda', kind: 'rRise', npc: '', x: e.x, y: e.y, n: bare ? 0 : heal });
    cue(`rise${w.dives}`);
    return;
  }
  if (w.mode === 4) {
    if (s.tick >= w.until) w.mode = 0;
    return;
  }
  if (w.mode === 5) {
    if (s.tick >= w.until) {
      w.mode = 6;
      w.until = s.tick + r.leap.air;
      w.leapFrom = { x: e.x, y: e.y };
    }
    return;
  }
  if (w.mode === 6) {
    const left = w.until - s.tick;
    if (left > 0) {
      const k = r.leap.air - left;
      e.x = w.leapFrom.x + idiv((w.leapAt.x - w.leapFrom.x) * k, r.leap.air);
      e.y = w.leapFrom.y + idiv((w.leapAt.y - w.leapFrom.y) * k, r.leap.air);
      return;
    }
    e.x = w.leapAt.x;
    e.y = w.leapAt.y;
    w.mode = 0;
    w.leapNext = s.waveT + (w.phase === 3 ? r.leap.last : r.leap.every);
    const hit = !!h && withinRange(h.x, h.y, e.x, e.y, r.leap.r);
    if (h && hit) damageHero(s, h, mulBp(h.stats.maxHp, r.leap.bp), e, 'physical', true);
    ev(s, { t: 'senda', kind: 'rLeap', npc: '', x: e.x, y: e.y, n: hit ? 1 : 0 });
    return;
  }
  if (w.mode === 7 || w.mode === 8) {
    // (Its blows cut it only while it howls: the bristling is the warning.)
    if (w.mode === 8 && w.howlHp - e.hp >= mulBp(e.maxHp, r.howl.cutBp)) return cutHowl(s, r, w, e, 0, cue);
    if (s.tick < w.until) return;
    if (w.mode === 7) {
      w.mode = 8;
      w.howlHp = e.hp;
      w.until = s.tick + r.howl.howl;
      ev(s, { t: 'senda', kind: 'rHowl', npc: '', x: e.x, y: e.y, n: r.howl.howl });
      return;
    }
    w.mode = 0;
    w.howlNext = s.waveT + r.howl.every;
    raise(s, r, w);
    ev(s, { t: 'senda', kind: 'rHowled', npc: '', x: e.x, y: e.y });
    cue('howled1');
    return;
  }
  if (w.mode === 9) {
    if (withinRange(e.x, e.y, r.crackTop.x, r.crackTop.y, 500)) w.mode = 0;
    return;
  }
  if (w.mode === 10) {
    if (e.hp < w.hpWas) w.boatHits++;
    w.hpWas = e.hp;
    const back = !!h && idiv(h.hp * 10000, imax(1, h.stats.maxHp)) >= r.boat.back;
    if (w.boatHits >= r.boat.caught || back || !h) {
      w.mode = 0;
      w.boatRunNext = s.waveT + r.boat.again;
      return;
    }
    if (!withinRange(e.x, e.y, r.skiff.x, r.skiff.y, 600)) return;
    if (w.gnawCd > 0) w.gnawCd--;
    else {
      w.gnawCd = r.boat.gnaw;
      const b = rocaNpc(s, 'skiff');
      if (b) {
        hurtNpc(s, b);
        ev(s, { t: 'senda', kind: 'rGnaw', npc: 'skiff', x: r.skiff.x, y: r.skiff.y, n: b.hp });
        if (b.hp <= 0) {
          w.lost = s.tick;
          cue('skiffLost');
        }
      }
    }
  }
};

/** The Wolf by its own rule (true: the act moved it). */
export const wolfHold = (s: BattleState, e: Enemy): boolean => {
  const r = wr(s);
  const w = ws(s);
  if (!r || !w || e.id !== w.id) return false;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (w.mode === 3) {
    e.anim = 'idle';
    return true;
  }
  // (Stunned, the base counts its stun down and keeps it still.)
  if (e.stun > 0) return false;
  if (w.mode === 4 || w.mode === 11) {
    e.anim = 'idle';
    return true;
  }
  if (w.mode === 1 || w.mode === 5 || w.mode === 7 || w.mode === 8) {
    const t = w.mode === 1 ? wolfExitAt(r, w.exitKind, w.exit) : w.mode === 5 ? w.leapAt : null;
    if (t && (t.x !== e.x || t.y !== e.y)) e.facing = iatan2(t.y - e.y, t.x - e.x);
    e.anim = 'cast';
    return true;
  }
  if (w.mode === 6) {
    e.anim = 'walk';
    return true;
  }
  if (w.mode === 2 || w.mode === 9 || w.mode === 10) {
    const p = w.mode === 2 ? wolfExitAt(r, w.exitKind, w.exit) : w.mode === 9 ? r.crackTop : r.skiff;
    // Running for the water as fast as the hero (slower for each blow on the way and each scar); up the crack, for the boat: as fast.
    const scar = imin(5000, r.scars * r.scarSlowBp);
    const pace = w.mode === 2 ? idiv(mulBp(r.flee, imax(r.minSlowBp, 10000 - w.slowBp - scar)), 12) : idiv(mulBp(r.flee, w.mode === 10 ? r.huntBp : 10000), 12);
    if (withinRange(e.x, e.y, p.x, p.y, 120)) e.anim = w.mode === 10 ? 'attack' : 'idle';
    else {
      e.anim = 'walk';
      e.facing = iatan2(p.y - e.y, p.x - e.x);
      stepToward(e, p.x, p.y, pace);
    }
    return true;
  }
  if (!h) {
    e.anim = 'idle';
    return true;
  }
  return bite(s, r, w, e, h);
};

/** The Wolf's life taken, of all of it. */
export const wolfGoal = (s: BattleState): { done: number; need: number } => {
  const w = ws(s);
  const e = w ? s.enemies.find((x) => x.id === w.id) : undefined;
  if (!w || !e) return { done: w && w.done >= 0 ? 1 : 0, need: 1 };
  return { done: e.maxHp - imax(0, e.hp), need: e.maxHp };
};

export const wolfResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = ws(s);
  if (!w) return null;
  if (w.lost >= 0 && s.tick >= w.lost + 12) return 'lost';
  return w.done >= 0 && s.tick >= w.done + 24 ? 'won' : null;
};
